import React, { useEffect, useState } from 'react';
import Filters from '../../../components/Filter';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { CRMParamList } from '../../../typings/Params';
import FilterNumericRange from '../../../components/Filter/components/FilterNumericRange';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import moment from 'moment';
import PolygonTypeDropdown from '../../../components/PolygonTypeDropdown';
import PolygonByTypeDropdown from '../../../components/PolygonByTypeDropdown';
import MyDateRangePicker from '../../../components/MyDateRangePicker';
import { useForm } from 'react-hook-form';

interface Props {
    filters: CRMParamList['CameraHistory'] | CRMParamList['OfflineEvents'];
    setFilters: React.Dispatch<React.SetStateAction<CRMParamList['CameraHistory'] | CRMParamList['OfflineEvents']>>;
    authenticatedUser?: AuthenticatedUser;
    textFilter: string;
    setTextFilter: (value: string) => void;
    showTextFilter: boolean;
}

type ValuePiece = Date | null;

interface FiltersForm {
    start: { from: ValuePiece; to: ValuePiece; };
    finish: { from: ValuePiece; to: ValuePiece; };
}

function Filter({ filters, setFilters, textFilter, setTextFilter, showTextFilter }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<CRMParamList, 'CameraHistory' | 'OfflineEvents'>>>();
    const [fromDuration, setFromDuration] = useState<number | null>(filters.fromDuration || null);
    const [toDuration, setToDuration] = useState<number | null>(filters.toDuration || null);
    const [onlyOffline, setOnlyOffline] = useState(filters.onlyOffline ?? false);
    const [selectedPolygonType, setSelectedPolygonType] = useState<string>(filters.polygonType ?? '');
    const [selectedPolygon, setSelectedPolygon] = useState<number>(filters.polygon ?? 0);
    const [polygonPendingLoading, setPolygonPendingLoading] = useState<boolean>(false);

    const defaultEmptyValues: FiltersForm = {
        start: { from: new Date(), to: new Date() },
        finish: { from: new Date(), to: new Date() },
    };

    const form = useForm<FiltersForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    useEffect(() => {

        form.reset({
            start: {
                from: filters.fromStart ? new Date(filters.fromStart) : null,
                to: filters.toStart ? new Date(filters.toStart) : null,
            },
            finish: {
                from: filters.fromFinish ? new Date(filters.fromFinish) : null,
                to: filters.toFinish ? new Date(filters.toFinish) : null,
            },
        });

    }, []);

    const handleCleanFilter = () => {
        setTextFilter('');
        setToDuration(null);
        setFromDuration(null);
        setOnlyOffline(false);
        form.reset({
            start: {
                from: null,
                to: null,
            },
            finish: {
                from: null,
                to: null,
            },
        });
        setSelectedPolygonType('');
        setSelectedPolygon(0);
    };

    const handleFilter = () => {

        const values = form.getValues();

        navigation.setParams({
            ...filters,
            page: 1,
            textFilter,
            toDuration: toDuration ?? undefined,
            fromDuration: fromDuration ?? undefined,
            onlyOffline,
            fromStart: values.start.from ? values.start.from.getTime() : undefined,
            toStart: values.start.to ? values.start.to.getTime() : undefined,
            fromFinish: values.finish.from ? values.finish.from.getTime() : undefined,
            toFinish: values.finish.to ? values.finish.to.getTime() : undefined,
            polygonType: selectedPolygonType !== '' ? selectedPolygonType : undefined,
            polygon: selectedPolygon != 0 ? selectedPolygon : undefined
        });

        setFilters({
            ...filters,
            page: 0,
            textFilter,
            toDuration: toDuration ?? undefined,
            fromDuration: fromDuration ?? undefined,
            onlyOffline: onlyOffline,
            fromStart: values.start.from ? values.start.from.getTime() : undefined,
            toStart: values.start.to ? values.start.to.getTime() : undefined,
            fromFinish: values.finish.from ? values.finish.from.getTime() : undefined,
            toFinish: values.finish.to ? values.finish.to.getTime() : undefined,
            polygonType: selectedPolygonType !== '' ? selectedPolygonType : undefined,
            polygon: selectedPolygon != 0 ? selectedPolygon : undefined,
        });
    };

    return (
        <Filters
            onCleanFilters={handleCleanFilter}
            onFilters={handleFilter}
            applyIsLoading={polygonPendingLoading}
            applyDisabled={selectedPolygonType != '' && selectedPolygon == 0}
        >
            {showTextFilter &&
                <FilterInput
                    label={translate('typeToSearch')}
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                />
            }
            <FilterCheckBox
                label={translate('onlyOpenedTickets')}
                value={onlyOffline}
                onChange={setOnlyOffline}
            />
            <FilterNumericRange
                label={`${translate('duration')}(${translate('minutes')})`}
                onFromChange={setFromDuration}
                fromValue={fromDuration}
                onToChange={setToDuration}
                toValue={toDuration}
            />
            <MyDateRangePicker
                label={translate('startBetween')}
                maxDate={moment().toDate()}
                form={form}
                fieldName='start'
                zIndex={5}
            />
            <MyDateRangePicker
                label={translate('finishBetween')}
                maxDate={moment().toDate()}
                form={form}
                fieldName='finish'
                zIndex={4}
            />

            <PolygonTypeDropdown
                label={translate('regions')}
                dropDownDirection='BOTTOM'
                showTooltip
                tooltipText={translate('regionsInfo')}
                zIndex={3}
                setPolygonType={setSelectedPolygonType}
                polygonType={selectedPolygonType}
            />
            {selectedPolygonType != '' &&
                <PolygonByTypeDropdown
                    label={translate('subRegions')}
                    dropDownDirection='BOTTOM'
                    setPolygon={setSelectedPolygon}
                    polygon={selectedPolygon}
                    polygonType={selectedPolygonType}
                    showTooltip
                    tooltipText={translate('subRegionsInfo')}
                    zIndex={2}
                    setPendingLoading={setPolygonPendingLoading}
                />
            }

        </Filters>
    );
}

export default Filter;
